import { useRef, useState, type FormEvent } from 'react';
import { LeadDeliveryError, recordLead, type LeadRecord } from '../lib/lead-channel';
import type { SiteDictionary, SiteLanguage } from '../content/site-content';

type Errors = Readonly<Record<string, string>>;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function ConsolidatedLeadForm({
  dictionary,
  language
}: {
  dictionary: SiteDictionary;
  language: SiteLanguage;
}) {
  const t = (key: string) => dictionary[key] ?? key;
  const summaryRef = useRef<HTMLDivElement>(null);
  const [errors, setErrors] = useState<Errors>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [state, setState] = useState<'idle' | 'submitting' | 'done'>('idle');
  const [record, setRecord] = useState<LeadRecord | null>(null);

  const validate = (data: FormData) => {
    const found: Record<string, string> = {};
    for (const name of ['nombre', 'empresa', 'correo', 'contexto']) {
      if (!String(data.get(name) ?? '').trim()) found[name] = t('form.invalid');
    }
    const email = String(data.get('correo') ?? '').trim();
    if (email && !EMAIL_PATTERN.test(email)) found.correo = t('form.invalid');
    if (data.get('consentimiento') !== 'on') found.consentimiento = t('form.invalid');
    return found;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const found = validate(data);
    setErrors(found);
    setFailure(null);

    if (Object.keys(found).length > 0) {
      window.requestAnimationFrame(() => summaryRef.current?.focus());
      return;
    }

    setState('submitting');
    try {
      const written = await recordLead(data, language);
      setRecord(written);
      setState('done');
    } catch (error) {
      const reason = error instanceof LeadDeliveryError ? error.reason : 'network';
      setFailure(
        reason === 'storage'
          ? t('form.storageError')
          : reason === 'rejected'
            ? t('form.rejectedError')
            : t('form.networkError')
      );
      setState('idle');
      window.requestAnimationFrame(() => summaryRef.current?.focus());
    }
  };

  if (state === 'done') {
    return (
      <div className="lead-form lead-form__receipt" role="status">
        <p className="form-kicker">{t('form.review')}</p>
        <h3>{t('form.successTitle')}</h3>
        <p>{t('form.success')}</p>
        {record ? <p className="lead-form__receipt-id">ID · {record.id}</p> : null}
        <button
          className="action action--solid"
          type="button"
          onClick={() => {
            setRecord(null);
            setState('idle');
          }}
        >
          <span>{t('form.reset')}</span><span aria-hidden="true">↗</span>
        </button>
      </div>
    );
  }

  const summary = failure ?? (Object.keys(errors).length ? t('form.invalid') : '');
  const errorFor = (name: string) => errors[name] ?? '';

  return (
    <form
      className="lead-form"
      noValidate
      aria-labelledby="form-title"
      onSubmit={onSubmit}
      data-contact-form
    >
      <div className="lead-form__summary" ref={summaryRef} tabIndex={-1} role="alert">
        {summary}
      </div>

      <div className="lead-form__review">
        <p className="form-kicker">{t('form.review')}</p>
        <p>{t('form.notice')}</p>
      </div>

      <div className="lead-form__heading">
        <span>01</span>
        <h3 id="form-title">{t('form.title')}</h3>
      </div>

      <div className="lead-form__fields">
        <label className="lead-field">
          <span className="lead-field__label"><span>{t('form.name')}</span><i>·</i></span>
          <input type="text" name="nombre" aria-label={t('form.name')} autoComplete="name" required aria-invalid={Boolean(errors.nombre) || undefined} />
          <small data-field-error>{errorFor('nombre')}</small>
        </label>
        <label className="lead-field">
          <span className="lead-field__label"><span>{t('form.company')}</span><i>·</i></span>
          <input type="text" name="empresa" aria-label={t('form.company')} autoComplete="organization" required aria-invalid={Boolean(errors.empresa) || undefined} />
          <small data-field-error>{errorFor('empresa')}</small>
        </label>
        <label className="lead-field">
          <span className="lead-field__label">
            <span>{t('form.role')}</span><i className="lead-field__optional">{t('form.optional')}</i>
          </span>
          <input type="text" name="cargo" aria-label={t('form.role')} autoComplete="organization-title" />
          <small data-field-error />
        </label>
        <label className="lead-field">
          <span className="lead-field__label"><span>{t('form.email')}</span><i>·</i></span>
          <input type="email" name="correo" aria-label={t('form.email')} autoComplete="email" required aria-invalid={Boolean(errors.correo) || undefined} />
          <small data-field-error>{errorFor('correo')}</small>
        </label>
        <label className="lead-field">
          <span className="lead-field__label">
            <span>{t('form.phone')}</span><i className="lead-field__optional">{t('form.optional')}</i>
          </span>
          <input type="tel" name="telefono" aria-label={t('form.phone')} autoComplete="tel" />
          <small className="lead-field__hint">{t('form.phoneHint')}</small>
        </label>
        <label className="lead-field">
          <span className="lead-field__label">
            <span>{t('form.team')}</span><i className="lead-field__optional">{t('form.optional')}</i>
          </span>
          <select name="equipo" aria-label={t('form.team')} defaultValue="">
            <option value="">{t('form.select')}</option>
            <option value="1-5">1 a 5</option>
            <option value="6-20">6 a 20</option>
            <option value="21-60">21 a 60</option>
            <option value="60+">60+</option>
          </select>
          <small data-field-error />
        </label>
        <label className="lead-field lead-field--wide">
          <span className="lead-field__label">
            <span>{t('form.cost')}</span><i className="lead-field__optional">{t('form.optional')}</i>
          </span>
          <select name="interes" aria-label={t('form.cost')} defaultValue="">
            <option value="">{t('form.select')}</option>
            <option value="unknown">{t('form.cost1')}</option>
            <option value="fragmented">{t('form.cost2')}</option>
            <option value="knowledge">{t('form.cost3')}</option>
            <option value="pipeline">{t('form.cost4')}</option>
            <option value="response">{t('form.cost5')}</option>
            <option value="repetition">{t('form.cost6')}</option>
          </select>
          <small data-field-error />
        </label>
        <label className="lead-field lead-field--wide">
          <span className="lead-field__label"><span>{t('form.context')}</span><i>·</i></span>
          <textarea name="contexto" aria-label={t('form.context')} rows={5} required aria-invalid={Boolean(errors.contexto) || undefined} />
          <small className="lead-field__hint">{t('form.contextHint')}</small>
          <small data-field-error>{errorFor('contexto')}</small>
        </label>
      </div>

      <label className="lead-consent" data-invalid={errors.consentimiento ? 'true' : undefined}>
        <input type="checkbox" name="consentimiento" required aria-invalid={Boolean(errors.consentimiento) || undefined} />
        <span>{t('form.consent')}</span>
      </label>

      <p className="lead-form__trap" aria-hidden="true">
        <label htmlFor="civili-site-trap">No completar</label>
        <input id="civili-site-trap" name="sitio" type="text" tabIndex={-1} autoComplete="off" />
      </p>

      {/* La autorización de la Ley 1581 tiene que poder leerse antes de darla:
          el enlace a la política no es decoración, es lo que hace informado al
          consentimiento de la casilla de arriba. */}
      <p className="lead-form__privacy">
        {t('form.privacy')}{' '}
        <a href={language === 'en' ? '/privacy' : '/privacidad'}>{t('form.privacyLink')}</a>
      </p>

      <div className="lead-form__action">
        <button className="action action--solid" type="submit" disabled={state === 'submitting'}>
          <span>{state === 'submitting' ? t('form.submitting') : t('form.submit')}</span><span aria-hidden="true">↗</span>
        </button>
      </div>

      <p className="lead-form__note">{t('form.channelNote')}</p>
    </form>
  );
}